import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../schema.dexie";
import { getConfig, getCriancaAtiva } from "../../db/queries";
import { diasAtras } from "../../lib/format";
import { filaRotinaMusical } from "../domain";
import { confirmarMomentoComPasso, descartarMomento, getMomentosPendentes } from "../queries";
import {
  PassoEngajamento,
  type ExperienciaMusical,
  type MomentoMusical,
} from "../types";
import { EngajamentoSelector } from "../ui/EngajamentoSelector";
import { ExperienciaFoto } from "../ui/ExperienciaFoto";
import { IndicadorEngajamento } from "../ui/IndicadorEngajamento";

/**
 * Compasso · Hoje — a ação primária (registrar momento) a um toque, lembrete de
 * rotina musical (P0.6), confirmação das escolhas da criança (P0.3) e panorama do
 * repertório. Espelha a tela Hoje do módulo alimentar.
 */
export function HojeMusica() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const experiencias = useLiveQuery(() => db.experiencia.toArray()) ?? [];
  const momentos = useLiveQuery(() => db.momento.toArray()) ?? [];
  const pendentes = useLiveQuery(getMomentosPendentes) ?? [];

  const cadencia = cfg?.musica_rotina_dias ?? 5;
  const retomar = filaRotinaMusical(experiencias, momentos, cadencia);

  const ativas = experiencias.filter((e) => !e.arquivado);
  const nFavoritas = ativas.filter((e) => e.status === "favorita").length;
  const nExploracao = ativas.filter((e) => e.status === "em_exploracao").length;
  const nSensiveis = ativas.filter((e) => e.status === "sensivel").length;

  const expDe = (id: string) => experiencias.find((e) => e.id === id) ?? null;

  return (
    <main className="tela">
      <div className="row-entre">
        <h1 className="tela-titulo">
          Compasso{crianca ? ` · ${crianca.apelido}` : ""}
        </h1>
        <Link to="/compasso/config" className="btn btn-fantasma" aria-label="configurações">
          ⚙️
        </Link>
      </div>

      {ativas.length === 0 && (
        <div className="card boas-vindas">
          <div className="bv-titulo">um caderno de campo musical 🎵</div>
          <p className="bv-sub">como funciona, em 3 passos:</p>
          <ol className="bv-passos">
            <li>
              tenha experiências na <Link to="/compasso/biblioteca">biblioteca</Link>{" "}
              (já vem um repertório inicial).
            </li>
            <li>
              <strong>registre o momento</strong>: toque o passo de engajamento (0–9).
            </li>
            <li>
              veja a <strong>evolução</strong> e descubra o que acalma e o que ativa.
            </li>
          </ol>
          <p className="bv-rodape">organiza dados — não substitui o musicoterapeuta.</p>
        </div>
      )}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        style={{ marginBottom: "var(--s4)" }}
        onClick={() => nav("/compasso/registrar")}
      >
        🎵 registrar momento musical
      </button>

      <button
        className="btn btn-bloco btn-grande"
        style={{ marginBottom: "var(--s5)" }}
        onClick={() => nav("/compasso/crianca")}
      >
        🧒 modo escolha da criança
      </button>

      {ativas.length > 0 && (
        <section className="panorama">
          <Link to="/compasso/biblioteca?status=favorita" className="stat stat-seguro">
            <span className="stat-num">{nFavoritas}</span>
            <span className="stat-rot">favoritas</span>
          </Link>
          <Link to="/compasso/biblioteca?status=em_exploracao" className="stat stat-teste">
            <span className="stat-num">{nExploracao}</span>
            <span className="stat-rot">em exploração</span>
          </Link>
          <Link to="/compasso/biblioteca?status=sensivel" className="stat">
            <span className="stat-num">{nSensiveis}</span>
            <span className="stat-rot">sensíveis</span>
          </Link>
        </section>
      )}

      {ativas.length > 0 && (
        <Link to="/compasso/kit" className="btn btn-bloco" style={{ marginBottom: "var(--s5)" }}>
          🧰 kit rápido — acalmar / ativar / favoritas
        </Link>
      )}

      {/* P0.3 — confirmar escolhas da criança: o cuidador marca o passo observado */}
      {pendentes.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">escolhas da criança pra confirmar</h2>
          {pendentes.map((m) => (
            <ConfirmarEscolha key={m.id} momento={m} experiencia={expDe(m.experiencia_id)} />
          ))}
        </section>
      )}

      {/* P0.6 — lembrete de rotina musical */}
      <section className="secao">
        <h2 className="secao-titulo">que tal retomar hoje?</h2>
        {retomar.length === 0 ? (
          <div className="card">
            <span style={{ color: "var(--text-muted)" }}>nada pendente — tudo em dia 🎵</span>
          </div>
        ) : (
          retomar.slice(0, 5).map(({ experiencia, dias_desde_ultimo }) => (
            <button
              className="card linha-alimento"
              key={experiencia.id}
              onClick={() => nav(`/compasso/registrar/${experiencia.id}`)}
            >
              <ExperienciaFoto
                midias={experiencia.midias}
                nome={experiencia.nome}
                tipo={experiencia.tipo}
              />
              <div className="crescer">
                <div className="nome">{experiencia.nome}</div>
                <div className="meta">{diasAtras(dias_desde_ultimo)}</div>
                <IndicadorEngajamento experienciaId={experiencia.id} momentos={momentos} />
              </div>
              <span aria-hidden>🎵</span>
            </button>
          ))
        )}
      </section>
    </main>
  );
}

/**
 * P0.3 — confirmação de uma escolha da criança. A escolha nasce pendente no passo
 * "exploração" (placeholder); aqui o cuidador marca o passo de engajamento que de
 * fato observou antes de confirmar — é o dado clínico real, não um chute do sistema.
 */
function ConfirmarEscolha({
  momento,
  experiencia,
}: {
  momento: MomentoMusical;
  experiencia: ExperienciaMusical | null;
}) {
  const [passo, setPasso] = useState<PassoEngajamento>(momento.passo_engajamento);

  return (
    <div className="card">
      <div className="row">
        <ExperienciaFoto
          midias={experiencia?.midias}
          nome={experiencia?.nome}
          tipo={experiencia?.tipo}
        />
        <div className="crescer">
          <div style={{ fontWeight: 500 }}>{experiencia?.nome ?? "experiência"}</div>
          <div className="meta" style={{ color: "var(--text-muted)" }}>
            a criança escolheu · marque o engajamento que você observou
          </div>
        </div>
      </div>

      <div style={{ marginTop: "var(--s3)" }}>
        <EngajamentoSelector valor={passo} onChange={setPasso} />
      </div>

      <div className="row" style={{ marginTop: "var(--s3)" }}>
        <button
          className="btn btn-primario crescer"
          onClick={() => confirmarMomentoComPasso(momento.id, passo)}
        >
          confirmar
        </button>
        <button className="btn" onClick={() => descartarMomento(momento.id)}>
          descartar
        </button>
      </div>
    </div>
  );
}
